import React, { useEffect, useRef, useCallback } from "react";
import { FaTimes } from "react-icons/fa";
import { colors, shadows, radius, zIndex, spacing, typography, transitions } from "../utils/designTokens";

/**
 * Modal unificado para el panel admin.
 *
 * Props:
 * - open (boolean): controla visibilidad
 * - onClose (function): callback al cerrar
 * - title (string): título del modal
 * - subtitle (string, opcional): subtítulo
 * - size ("narrow" | "medium" | "wide" | "full"): ancho máximo. "full" es para
 *   tablas editables grandes: casi toda la pantalla, también en alto.
 * - children: contenido del body
 * - footer (ReactNode, opcional): contenido del footer (botones)
 * - icon (ReactElement, opcional): icono junto al título
 */
const SIZES = {
  narrow: 480,
  medium: 640,
  wide: 800,
  full: 1440,
};

const AdminModal = ({
  open,
  onClose,
  title,
  subtitle,
  size = "medium",
  children,
  footer,
  icon,
  hideClose = false,
}) => {
  const overlayRef = useRef(null);
  const modalRef = useRef(null);
  const previousFocusRef = useRef(null);

  // Focus trap
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key !== "Tab") return;

      const focusable = modalRef.current?.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable || focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    },
    [onClose]
  );

  // Abrir: guardar foco previo, enfocar modal, bloquear scroll
  useEffect(() => {
    if (open) {
      previousFocusRef.current = document.activeElement;
      document.body.style.overflow = "hidden";
      // Enfocar primer elemento focusable después de render
      requestAnimationFrame(() => {
        const focusable = modalRef.current?.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusable && focusable.length > 0) {
          focusable[0].focus();
        }
      });
    } else {
      document.body.style.overflow = "";
      if (previousFocusRef.current) {
        previousFocusRef.current.focus();
      }
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  // Listener de teclado
  useEffect(() => {
    if (open) {
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [open, handleKeyDown]);

  if (!open) return null;

  const maxWidth = SIZES[size] || SIZES.medium;

  return (
    <div
      ref={overlayRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="admin-modal-title"
      style={styles.overlay}
      onClick={(e) => {
        if (e.target === overlayRef.current) onClose();
      }}
    >
      <div
        ref={modalRef}
        style={{ ...styles.modal, maxWidth, ...(size === "full" ? { maxHeight: "94vh", height: "94vh" } : {}) }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerContent}>
            {icon && <span style={styles.headerIcon}>{icon}</span>}
            <div>
              <h2 id="admin-modal-title" style={styles.title}>
                {title}
              </h2>
              {subtitle && <p style={styles.subtitle}>{subtitle}</p>}
            </div>
          </div>
          {!hideClose && (
            <button
              onClick={onClose}
              style={styles.closeBtn}
              className="habitia-btn-ghost"
              aria-label="Cerrar modal"
            >
              <FaTimes size={16} />
            </button>
          )}
        </div>

        {/* Body */}
        <div style={styles.body}>{children}</div>

        {/* Footer */}
        {footer && <div style={styles.footer}>{footer}</div>}
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: spacing[4],
    zIndex: zIndex.modal,
  },
  modal: {
    width: "100%",
    background: colors.white,
    borderRadius: radius.lg,
    boxShadow: shadows.modal,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    maxHeight: "85vh",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: `${spacing[4]}px ${spacing[6]}px`,
    borderBottom: `1px solid ${colors.gray200}`,
    flexShrink: 0,
  },
  headerContent: {
    display: "flex",
    alignItems: "center",
    gap: spacing[3],
  },
  headerIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 36,
    height: 36,
    borderRadius: radius.sm,
    background: colors.primaryLighter,
    color: colors.primary,
    flexShrink: 0,
  },
  title: {
    fontSize: typography.sizes.xl,
    fontWeight: 400,
    color: colors.black,
    margin: 0,
    lineHeight: 1.3,
  },
  subtitle: {
    fontSize: typography.sizes.base,
    color: colors.gray600,
    margin: "2px 0 0",
  },
  closeBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 36,
    height: 36,
    borderRadius: radius.sm,
    border: "none",
    background: "transparent",
    color: colors.gray400,
    cursor: "pointer",
    transition: `all ${transitions.fast}`,
    flexShrink: 0,
  },
  body: {
    padding: spacing[6],
    overflowY: "auto",
    flex: 1,
    minHeight: 0,
  },
  footer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: spacing[3],
    padding: `${spacing[4]}px ${spacing[6]}px`,
    borderTop: `1px solid ${colors.gray200}`,
    background: colors.gray50,
    flexShrink: 0,
  },
};

export default AdminModal;
